import styles from './N8NConnection.module.css';

import Card, { CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/Card/Card';
import BasicAuthSection from '../authSections/BasicAuthSection';
import OAuthClientCredentialsSection from '../authSections/OAuthClientCredentialsSection';
import OboJwtSection from '../authSections/OboJwtSection';
import SamlBearerSection from '../authSections/SamlBearerSection';
import ApiKeyAuthSection from '../authSections/ApiKeyAuthSection';
import { useNavigate, useParams } from 'react-router-dom';
import { useBackNavigation } from '../../../hooks/useBackNavigation';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from '../../../lib/toast';
import { configApi } from '../../../lib/apiClient';
import { useAuthStore } from '../../../store/authStore';
import { useConnectionsStore } from '../../../store/connectionsStore';
import { authFieldsSchema } from '../authSections/types';
import { testConnectionApi } from '../../../lib/testConnection';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import { Link as LinkIcon } from 'lucide-react';
import { UserRole } from '../../../enums/userRole';
import { hasMinRole } from '../../../lib/permissions';


const n8nSchema = z.object({
    name: z.string().min(1, 'Connection name is required'),
    environment: z.enum(['test', 'prod', 'dev']).optional(),
    url: z.string().url('Invalid URL'),
    authType: z.enum(['none', 'api_key', 'basic', 'oauth2_client_credentials', 'oauth2_obo_jwt', 'saml2_bearer']),
    testPath: z.string().optional().nullable(),
    ...authFieldsSchema.shape
});

type N8NForm = z.infer<typeof n8nSchema>;


export default function N8NConnection() {
    const navigate = useNavigate();
    const goBack = useBackNavigation('/connectors');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [config, setConfig] = useState<any | null>(null);
    const user = useAuthStore(state => state.user)
    const { connectors, updateConnection } = useConnectionsStore(state => state);
    const { configId } = useParams();

    const { handleSubmit, setValue, watch, formState: { errors }, setError } = useForm<N8NForm>({
        resolver: zodResolver(n8nSchema),
        defaultValues: {
            name: '',
            environment: 'prod' as 'test' | 'prod' | 'dev',
            url: '',
            authType: 'basic',
            testPath: '',
            basic: { username: '', password: '' },
            oauth: { token_url: '', client_id: '', client_secret: '', scope: '' },
            obo: { token_url: '', audience: '', resource: '', issuer_discovery: '' },
            saml: { token_url: '', issuer: '', audience: '', private_key: '', certificate: '', client_id: '', scope: '', client_username: '', client_password: '', testNameId: '' },
            apikey: { key_name: '', key_value: '', in: 'header' },
        },
    });
    const validAuthTypes = ['none', 'api_key', 'basic', 'oauth2_client_credentials', 'oauth2_obo_jwt', 'saml2_bearer'] as const;
    const authType = watch('authType') || 'basic';

    useEffect(() => {
        if (!user || !hasMinRole(user?.role?.id, UserRole.Admin)) {
            navigate('/');
        }
    }, [user])

    useEffect(() => {
        const configData = connectors.find(c => c.id === configId) || null;
        if (configData) {
            setConfig(configData)
            setValue('name', configData.connectionName || '');
            setValue('environment', (configData.environment as 'test' | 'prod' | 'dev') || 'prod');
            setValue('url', configData.config?.baseUrl || '');
            setValue(
                'authType',
                validAuthTypes.includes(configData.authType as any)
                    ? configData.authType as typeof validAuthTypes[number]
                    : 'basic'
            );
            setValue('basic', configData.authFields?.basic || { username: '', password: '' });
            setValue('oauth', configData.authFields?.oauth || { token_url: '', client_id: '', client_secret: '', scope: '' });
            setValue('obo', configData.authFields?.obo || { token_url: '', audience: '', resource: '', issuer_discovery: '' });
            setValue('saml', configData.authFields?.saml || { token_url: '', issuer: '', audience: '', private_key: '', certificate: '', client_id: '', scope: '', client_username: '', client_password: '', testNameId: '' });
            setValue('apikey', configData.authFields?.apikey || { key_name: '', key_value: '', in: 'header' });
            setValue('testPath', configData.config?.testPath || '');
        }
    }, [connectors, configId]);

    const onSubmit = async (data: N8NForm) => {
        if (!data.environment) {
            setError('environment', { type: 'manual', message: 'Environment is required' });
            toast.error('Please select an environment');
            return;
        }

        setSaving(true);
        try {
            // Build authFields object
            const authFields: any = {};
            if (data.authType === 'api_key') {
                authFields.apikey = data.apikey;
            } else if (data.authType === 'basic') {
                authFields.basic = data.basic;
            } else if (data.authType === 'oauth2_client_credentials') {
                authFields.oauth = data.oauth;
            } else if (data.authType === 'oauth2_obo_jwt') {
                authFields.obo = data.obo;
            } else if (data.authType === 'saml2_bearer') {
                authFields.saml = data.saml;
            }

            const payload = {
                connectionName: data.name,
                environment: data.environment,
                config: {
                    baseUrl: data.url,
                    testPath: data.testPath || '',
                },
                userId: user?.id!,
                authType: data.authType,
                authFields,
            };

            let response;
            if (!config) response = await configApi.post(payload);
            else response = await configApi.put(config.id, payload);

            setConfig(response);
            updateConnection(response);
            toast.success('Configuration saved successfully!');
        } catch (error: any) {
            toast.error(`Failed to save configuration: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        if (!config?.id) {
            toast.error('Please save configuration first');
            return;
            }

        // Get current form values
        const formData = watch();
        
        // Build authFields object
        const authFields: any = {};
        if (formData.authType === 'api_key') {
            authFields.apikey = formData.apikey;
        } else if (formData.authType === 'basic') {
            authFields.basic = formData.basic;
        } else if (formData.authType === 'oauth2_client_credentials') {
            authFields.oauth = formData.oauth;
        } else if (formData.authType === 'oauth2_obo_jwt') {
            authFields.obo = formData.obo;
        } else if (formData.authType === 'saml2_bearer') {
            authFields.saml = formData.saml;
        }

        const connectionData = {
            connectionName: formData.name,
            environment: formData.environment,
            config: {
                baseUrl: formData.url,
                testPath: formData.testPath || '',
            },
            authType: formData.authType,
            authFields,
        };

        await testConnectionApi(config.id, setTesting, connectionData, user);
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1>n8n Connection</h1>
                    <p>Configure your n8n connection settings</p>
                </div>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        onClick={goBack}
                        className={styles.cancelBtn}
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={testConnection}
                        disabled={testing || !config}
                        className={styles.saveBtn}
                    >
                        {testing ? 'Testing...' : 'Test Connection'}
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit(onSubmit)}
                        disabled={saving}
                        className={styles.saveBtn}
                    >
                        {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                </div>
            </div>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                {/* BASIC SETTINGS */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Basic Settings</CardTitle>
                        <CardDescription>Core endpoint configuration used by all auth types.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="name"
                                label="Connection Name *"
                                value={watch('name') || ''}
                                onChange={(value) => setValue('name', value)}
                                required
                                className={styles.input}
                                externalError={errors.name?.message}
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Environment *</label>
                            <select
                                value={watch('environment') || ''}
                                onChange={(e) => {setValue('environment', e.target.value as 'test' | 'prod' | 'dev'); 
                                    setError('environment', { type: 'manual', message: '' }); }}
                                className={`${styles.select} ${errors.environment ? styles.selectError : ''}`}
                            >   
                                <option value="">Select environment</option>
                                <option value="prod">Prod</option>
                                <option value="test">Test</option>
                                <option value="dev">Dev</option>
                            </select>
                            {errors.environment && (
                                <span className={styles.error}>{errors.environment.message}</span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="url"
                                label="n8n URL *"
                                icon={<LinkIcon size={16} />}
                                value={watch('url') || ''}
                                onChange={(value) => setValue('url', value)}
                                required
                                className={styles.input}
                                externalError={errors.url?.message}
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Authentication Type *</label>
                            <select
                                value={watch('authType')}
                                onChange={(e) => {setValue('authType', e.target.value as any); 
                                    setError('authType', { type: 'manual', message: '' }); }}
                                className={`${styles.select} ${errors.authType ? styles.selectError : ''}`}
                            >
                                <option value="none">No Authentication</option>
                                <option value="api_key">API Key</option>
                                <optgroup label="Standard">
                                    <option value="oauth2_client_credentials">OAuth2 Client Credentials</option>
                                    <option value="basic">Basic Authentication</option>
                                </optgroup>
                                <optgroup label="Principal Propagation">
                                    <option value="oauth2_obo_jwt">OAuth2 On-Behalf-Of (JWT Bearer)</option>
                                    <option value="saml2_bearer">SAML2 Bearer Assertion</option>
                                </optgroup>
                            </select>
                            {errors.authType && (
                                <span className={styles.error}>{errors.authType.message}</span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="text"
                                label="Test Path (optional)"
                                value={watch('testPath') || ''}
                                onChange={(value) => setValue('testPath', value)}
                                placeholder="Relative path for connection testing"
                                className={styles.input}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* API KEY */}
                {authType === 'api_key' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>API Key</CardTitle>
                            <CardDescription>Send an API key as a header or query parameter.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ApiKeyAuthSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* BASIC AUTH */}
                {authType === 'basic' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>Basic Authentication</CardTitle>
                            <CardDescription>Use only for legacy systems and development.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <BasicAuthSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* OAUTH2 CLIENT CREDENTIALS */}
                {authType === 'oauth2_client_credentials' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>OAuth2 Client Credentials</CardTitle>
                            <CardDescription>Technical client for calling n8n APIs.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <OAuthClientCredentialsSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* OAUTH2 OBO JWT */}
                {authType === 'oauth2_obo_jwt' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>OAuth2 On-Behalf-Of (JWT Bearer)</CardTitle>
                            <CardDescription>Exchange IAS token for n8n access token.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <OboJwtSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* SAML2 BEARER */}
                {authType === 'saml2_bearer' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>SAML2 Bearer Assertion</CardTitle>
                            <CardDescription>Exchange a signed SAML assertion for access token.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <SamlBearerSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}
            </form>
        </div>
    );
}
